import { useId, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useUiStrings } from '../i18n'
import wordmarkCap from '../assets/wordmark-cap.svg'
import wordmarkHub from '../assets/wordmark-carrierhub.svg'
import type { Brand } from './ProductTile'

const WORDMARK: Record<Brand, string> = { cap: wordmarkCap, carrierhub: wordmarkHub }

/*
 * Fächerlinien der CAP-Module, Maße 1:1 aus „Element 617/618/619.svg“.
 * Die drei Teilgrafiken sind dort übereinander gestapelt: obere Kurve
 * (133,21 hoch), Mittellinie (9,02) und untere Kurve (133,21). Die Kurven
 * sind symmetrische S-Bögen mit waagerechten Anfassern auf halber Breite.
 */
const FAN = {
  w: 171.55,
  h: 275.44,
  xEnd: 167.04,
  cx: 83.52,
  r: 4.51,
  yTop: 132.21,
  yBottom: 143.23,
  dotTop: 4.51,
  dotMid: 137.72,
  dotBottom: 270.93,
};

/** Seitenverhältnis der Fächerlinien (Breite zu Höhe). */
export const CONNECTOR_FAN_RATIO = FAN.w / FAN.h;

/*
 * Im Entwurf sitzt jeder Punkt des Fächers auf halber Höhe des zugehörigen
 * Stichpunkts – nicht an dessen Ober- bzw. Unterkante. Legt man die drei
 * Stichpunkte als drei gleich hohe Zeilen an und zentriert den Text darin,
 * muss eine Zeile so hoch sein wie der Abstand zweier Punkte (133,21 von
 * 275,44 Einheiten). Die Liste ist damit 3 × 133,21 hoch, also gut das
 * 1,45-Fache des Fächers, und bleibt zu ihm mittig – die Mittellinie des
 * Fächers liegt genau auf halber Höhe.
 */
export const CONNECTOR_FAN_LABELS_SCALE =
  (3 * (FAN.dotMid - FAN.dotTop)) / FAN.h;

/**
 * Großer, halbtransparenter Produktname im Hintergrund des Heros.
 *
 * Im Entwurf steht der Schriftzug nicht mittig hinter der Kachel, sondern
 * deutlich höher: bei CAP um 36,5 %, beim Carrier Hub um 31,2 % (Desktop)
 * bzw. 53,7 % (mobil) der Kachelhöhe. Der Versatz kommt über die Variable
 * `--wm-shift` von außen, damit er sich je Breakpoint setzen lässt.
 */
export function Watermark({
  brand,
  className,
}: {
  brand: Brand
  className?: string
}) {
  return (
    <img
      src={WORDMARK[brand]}
      alt=""
      aria-hidden="true"
      style={{ transform: 'translate(-50%, calc(-50% + var(--wm-shift, 0px)))' }}
      className={cn(
        // Bezugspunkt ist die Mitte des umgebenden `relative`-Containers,
        // also die Kachelmitte.
        //
        // `mix-blend-multiply` bildet das Original nach: der Schriftzug dunkelt
        // den Seitenverlauf ab, statt hell darüber zu liegen. Deshalb bewusst
        // z-0 statt eines negativen z-index – sonst gäbe es keinen Hintergrund
        // zum Verrechnen. Geschwister brauchen entsprechend `relative z-10`.
        'pointer-events-none absolute top-1/2 left-1/2 z-0 max-w-none mix-blend-multiply select-none',
        className,
      )}
    />
  )
}

/** Doppelter Chevron als Hinweis „weiter unten geht es weiter“. */
export function ScrollCue({
  href,
  label,
  className,
}: {
  href?: string
  label?: string
  className?: string
}) {
  const t = useUiStrings()
  const icon = (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="5,7 12,13 19,7" />
      <polyline points="5,13 12,19 19,13" />
    </svg>
  )

  const classes = cn(
    'mx-auto flex w-fit items-center justify-center text-[var(--brand-accent)] opacity-80 transition-opacity hover:opacity-100',
    className,
  )

  if (!href) {
    return (
      <div className={classes} aria-hidden="true">
        {icon}
      </div>
    )
  }

  return (
    <a href={href} className={classes} aria-label={label ?? t.nextSection}>
      {icon}
    </a>
  )
}

/**
 * Dreieck-Paar zwischen den beiden Hälften der Startseite (Element 691/692).
 * Verläufe und Schlagschatten sind aus den Originalgrafiken übernommen.
 */
export function SplitCue({
  href,
  label = 'Zum nächsten Bereich',
  className,
}: {
  href?: string
  label?: string
  className?: string
}) {
  const triangle = (direction: 'up' | 'down', from: string, to: string) => (
    <svg
      viewBox="0 0 30 25.98"
      aria-hidden="true"
      className="h-[1.3rem] w-auto drop-shadow-[0_2px_5px_rgba(42,114,162,0.4)]"
    >
      <defs>
        <linearGradient
          id={`vcn-split-${direction}`}
          x1="0"
          y1="0"
          x2="30"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={from} />
          <stop offset={direction === 'up' ? '0.7' : '0.84'} stopColor={to} />
        </linearGradient>
      </defs>
      <polygon
        fill={`url(#vcn-split-${direction})`}
        points={direction === 'up' ? '0 25.98 30 25.98 15 0' : '30 0 0 0 15 25.98'}
      />
    </svg>
  )

  const content = (
    <>
      {triangle('up', '#ccffce', '#bee1f2')}
      {triangle('down', '#aaf6bc', '#c9ffcb')}
    </>
  )

  const classes = cn('mx-auto flex w-fit flex-col items-center gap-2.5', className)

  if (!href) {
    return (
      <div className={classes} aria-hidden="true">
        {content}
      </div>
    )
  }

  return (
    <a
      href={href}
      className={cn(classes, 'transition-opacity hover:opacity-80')}
      aria-label={label}
    >
      {content}
    </a>
  )
}

/** Kleiner Doppelpfeil zwischen zwei Prozess-Karten. */
export function StepArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn('size-5 shrink-0 text-[var(--brand-accent)] opacity-70', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="7,5 14,12 7,19" />
      <polyline points="13,5 20,12 13,19" />
    </svg>
  )
}

/** Kachel mit Plus-Icon aus den Nutzen-Listen. */
export function BenefitItem({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <li
      className={cn(
        'vcn-card flex items-start gap-3 px-5 py-4 text-[0.95rem] leading-snug text-white',
        className,
      )}
    >
      <svg
        viewBox="0 0 20 20"
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-[var(--brand-accent)]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <line x1="3" y1="10" x2="17" y2="10" />
        <line x1="10" y1="3" x2="10" y2="17" />
      </svg>
      <span className="text-pretty">{children}</span>
    </li>
  )
}

/**
 * Fächer zwischen Produktkachel und Stichpunkten.
 *
 * Geometrie aus den Originalgrafiken: je Kurve 173 × 134 Einheiten
 * (Element 617/618), dazwischen die gerade Linie (Element 619).
 *
 * Der Fächer muss sich in der Breite frei stauchen lassen – mobil ist er im
 * Entwurf deutlich schmaler als auf dem Desktop. Deshalb werden die Kurven mit
 * `preserveAspectRatio="none"` gezeichnet (Strichstärke bleibt über
 * `non-scaling-stroke` gleichmäßig) und die drei Endpunkte separat als runde
 * Elemente gesetzt – sonst würden sie zu Ellipsen verzerrt.
 */
export function ConnectorFan({ className }: { className?: string }) {
  const id = useId();

  return (
    <svg
      viewBox={`0 0 ${FAN.w} ${FAN.h}`}
      aria-hidden="true"
      // Seitenverhältnis aus der Geometrie: so bleiben die Bögen unverzerrt
      // und die Punkte rund. Die Höhe gibt der umgebende Container vor.
      style={{ aspectRatio: `${FAN.w} / ${FAN.h}` }}
      // Die Höhe gibt der Aufrufer vor, die Breite folgt dem Seitenverhältnis.
      // Gespiegelt wird über die Klasse von aussen (`-scale-x-100`) – nur der
      // Aufrufer weiss, ab welcher Breite seine Zeile die Seiten tauscht.
      className={cn("w-auto shrink-0", className)}
      fill="none"
    >
      <defs>
        {/* Wie im Original: Mint an der Kachel, Hellblau an den Stichpunkten. */}
        <linearGradient
          id={id}
          x1="0"
          y1="0"
          x2={FAN.xEnd}
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#ccffce" />
          <stop offset="1" stopColor="#bee1f2" />
        </linearGradient>
      </defs>

      <g stroke={`url(#${id})`} strokeWidth="2" strokeMiterlimit="10">
        <path
          d={`M0,${FAN.yTop} C${FAN.cx},${FAN.yTop} ${FAN.cx},${FAN.dotTop} ${FAN.xEnd},${FAN.dotTop}`}
        />
        <path d={`M0,${FAN.dotMid} H${FAN.xEnd}`} />
        <path
          d={`M0,${FAN.yBottom} C${FAN.cx},${FAN.yBottom} ${FAN.cx},${FAN.dotBottom} ${FAN.xEnd},${FAN.dotBottom}`}
        />
      </g>

      <g fill={`url(#${id})`}>
        {[FAN.dotTop, FAN.dotMid, FAN.dotBottom].map((cy) => (
          <circle key={cy} cx={FAN.xEnd} cy={cy} r={FAN.r} />
        ))}
      </g>
    </svg>
  );
}
